import type { ReminderNotice, SnoozeChoice } from '../types/reminders'

/*
  **Was fällig ist** — die reine Rechnung hinter der Zustellung (Plan task-reminders, Etappe 3).
  Getrennt von Electron und Browser, damit sie ohne Fenster und Uhr testbar ist.
*/

/** Verpasstes kommt nach, wenn es höchstens so alt ist (Entscheidung 8). */
export const MISSED_WINDOW_MS = 12 * 60 * 60 * 1000
/** Später als das gilt ein Termin als verpasst (Rechner schlief, App war zu). */
export const LATE_AFTER_MS = 2 * 60 * 1000
/** Längster Schlaf des Timers — Uhrzeitwechsel und Schlaf verschleppen ihn so höchstens eine Stunde. */
export const MAX_TIMER_MS = 60 * 60 * 1000
/** Gefeuerte Schlüssel so lange merken: länger, als ein Termin im Plan zurückliegen kann. */
const FIRED_MEMORY_MS = 15 * 24 * 60 * 60 * 1000

export interface DueNotices {
  /** Pünktlich — je eine Mitteilung. */
  onTime: ReminderNotice[]
  /** Verpasst, aber jünger als 12 Std. — gebündelt, wenn es mehrere sind. */
  missed: ReminderNotice[]
  /** Zu alt — verfällt still. */
  expired: ReminderNotice[]
}

/**
 * Alle Termine, die seit der letzten Prüfung fällig wurden und noch nicht klangen. Was vor
 * `lastCheckedAt` lag, war schon dran (oder wurde erst danach mit vergangener Zeit angelegt —
 * das klingelt nicht nachträglich). Beim allerersten Lauf (`null`) zählt nichts von vorher.
 */
export function dueNotices(notices: readonly ReminderNotice[], fired: ReadonlySet<string>, lastCheckedAt: number | null, now: number): DueNotices {
  const since = lastCheckedAt ?? now
  const due = notices.filter(n => !fired.has(n.key) && n.fireAt > since && n.fireAt <= now).sort((a, b) => a.fireAt - b.fireAt)
  const result: DueNotices = { onTime: [], missed: [], expired: [] }
  for (const notice of due) {
    const lateBy = now - notice.fireAt
    if (lateBy > MISSED_WINDOW_MS) result.expired.push(notice)
    else if (lateBy > LATE_AFTER_MS) result.missed.push(notice)
    else result.onTime.push(notice)
  }
  return result
}

/** Wann die nächste Prüfung ansteht: der nächste Termin, höchstens eine Stunde voraus. */
export function nextCheckIn(notices: readonly ReminderNotice[], fired: ReadonlySet<string>, now: number): number {
  const next = notices.reduce<number | null>((min, n) => (n.fireAt > now && !fired.has(n.key) && (min === null || n.fireAt < min) ? n.fireAt : min), null)
  return next === null ? MAX_TIMER_MS : Math.max(0, Math.min(next - now, MAX_TIMER_MS))
}

/** Bis wann „Später“ schiebt — „morgen“ heißt morgen 9:00 (wie Things 3.24). */
export function snoozeUntil(choice: SnoozeChoice, now: Date): Date {
  switch (choice) {
    case '15m': return new Date(now.getTime() + 15 * 60_000)
    case '1h': return new Date(now.getTime() + 60 * 60_000)
    case '3h': return new Date(now.getTime() + 3 * 60 * 60_000)
    case 'tomorrow': return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0)
  }
}

/** Der Schlüssel eines geschlummerten Termins — derselbe, den der Renderer im Plan führt. */
export const snoozeKey = (reminderId: string, until: number): string => `${reminderId}@snooze@${until}`

/** Vergisst gefeuerte Schlüssel, die kein Plan mehr enthalten kann. */
export function pruneFired(fired: Readonly<Record<string, number>>, now: number): Record<string, number> {
  return Object.fromEntries(Object.entries(fired).filter(([, at]) => now - at < FIRED_MEMORY_MS))
}
